<script>
import { assistantState, canRestoreCheckpoint, getActiveChat } from "@app/store/assistantStore.svelte.js";
import { renderAssistantMarkdown } from "@app/lib/assistant/markdown.js";
import { confirmRedoRestore, confirmRestoreToMessage } from "@app/lib/assistant/confirmRestore.js";
import { savedPromptState } from "@app/store/savedPromptStore.svelte.js";
import AssistantActivityCard from "./AssistantActivityCard.svelte";
import * as m from "@app/paraglide/messages.js";

const EMPTY_STATE_SAVED_PROMPTS = 5;

let chat = $derived(getActiveChat());
let messages = $derived(chat?.messages ?? []);
let attempts = $derived(chat?.previousAttempts ?? []);
let restoreEnabled = $derived(canRestoreCheckpoint());

const ASK_SUGGESTIONS = [
    () => m.assistant_suggest_ask_overlaps(),
    () => m.assistant_suggest_ask_cps(),
    () => m.assistant_suggest_ask_export(),
];
const AGENT_SUGGESTIONS = [
    () => m.assistant_suggest_agent_reading_rate(),
    () => m.assistant_suggest_agent_gaps(),
    () => m.assistant_suggest_agent_translate(),
    () => m.assistant_suggest_agent_qc(),
];
let suggestions = $derived((assistantState.mode === "agent" ? AGENT_SUGGESTIONS : ASK_SUGGESTIONS).map((text) => text()));

/** "Previous attempt" blocks that sit before the message at this index (or at the end). */
function attemptsAt(index) {
    return attempts.filter((attempt) =>
        index === messages.length ? attempt.atIndex >= messages.length : attempt.atIndex === index,
    );
}

function attemptText(message) {
    return typeof message.content === "string" ? message.content : "";
}

const ERROR_MESSAGES = {
    "resource-exhausted": () =>
        m.assistant_limit_reached({ limit: assistantState.status?.limit ?? "" }),
    unavailable: () => m.assistant_error_unavailable(),
    "permission-denied": () => m.assistant_error_permission(),
    unauthenticated: () => m.assistant_error_auth(),
    credits: () =>
        assistantState.status?.agent?.trial ? m.assistant_error_trial_credits() : m.assistant_error_credits(),
    overage_cap: () => m.assistant_error_overage_cap(),
    not_billing_owner: () => m.assistant_error_not_billing_owner(),
    step_limit: () => m.assistant_error_step_limit(),
    conversation_too_long: () => m.assistant_error_conversation_too_long(),
    agent_not_allowed: () => m.assistant_error_agent_not_allowed(),
};

const NOTICES = {
    max_tokens: () => m.assistant_agent_notice_max_tokens(),
    step_limit: () => m.assistant_agent_notice_step_limit(),
    refused: () => m.assistant_agent_notice_refused(),
};

function errorText(code) {
    return (ERROR_MESSAGES[code] ?? (() => m.assistant_error_generic()))();
}

function citationLabel(citation) {
    const parts = String(citation.title ?? "").split(" › ");
    return parts[parts.length - 1] || citation.url;
}

function scrollToEnd(node) {
    // Reading the newest message's text and activities re-runs this attachment as the reply streams in.
    const last = messages[messages.length - 1];
    void last?.content?.length;
    void last?.activities?.length;
    void last?.activities?.at(-1)?.status;
    node.scrollTop = node.scrollHeight;
}
</script>

<div class="assistant-messages flex-grow-1 overflow-auto p-3" {@attach scrollToEnd}>
    {#snippet previousAttempts(index)}
        {#each attemptsAt(index) as attempt (attempt.id)}
            <details class="assistant-attempt small text-muted mb-3">
                <summary>{m.assistant_previous_attempt({ count: attempt.messages.length })}</summary>
                <div class="mt-2 ps-2 border-start">
                    {#each attempt.messages as old (old.id)}
                        {#if attemptText(old)}
                            <div class={["mb-1", old.role === "user" && "fw-semibold"]}>{attemptText(old)}</div>
                        {/if}
                    {/each}
                </div>
            </details>
        {/each}
    {/snippet}

    {#if messages.length === 0 && attempts.length === 0}
        <div class="assistant-empty text-center px-3">
            <div class="assistant-empty-icon mx-auto mb-2"><i class="bi bi-stars" aria-hidden="true"></i></div>
            <h6 class="mb-1">{m.assistant_empty_title()}</h6>
            <p class="small text-muted">{assistantState.mode === "agent" ? m.assistant_agent_empty_text() : m.assistant_empty_text()}</p>
            {#if savedPromptState.length}
                <div class="assistant-empty-heading small text-muted text-start mt-3 mb-1">{m.assistant_saved_prompts()}</div>
                <div class="d-flex flex-column gap-1 align-items-stretch">
                    {#each savedPromptState.slice(0, EMPTY_STATE_SAVED_PROMPTS) as prompt (prompt.id)}
                        <button
                            type="button"
                            class="assistant-suggestion assistant-saved-suggestion text-start"
                            title={prompt.text}
                            onclick={() => (assistantState.composerDraft = { text: prompt.text, mode: prompt.mode })}
                        >
                            <i class={["bi me-2", prompt.mode === "agent" ? "bi-magic" : "bi-chat-dots"]} aria-hidden="true"></i>{prompt.text}
                        </button>
                    {/each}
                </div>
                <div class="assistant-empty-heading small text-muted text-start mt-3 mb-1">{m.assistant_example_prompts()}</div>
            {/if}
            <div class={["d-flex flex-column gap-1 align-items-stretch", !savedPromptState.length && "mt-3"]}>
                {#each suggestions as suggestion (suggestion)}
                    <button
                        type="button"
                        class="assistant-suggestion text-start"
                        onclick={() => (assistantState.composerDraft = { text: suggestion, mode: assistantState.mode })}
                    >
                        <i class={["bi me-2", assistantState.mode === "agent" ? "bi-magic" : "bi-chat-dots"]} aria-hidden="true"></i>{suggestion}
                    </button>
                {/each}
            </div>
        </div>
    {:else}
        {#each messages as message, index (message.id)}
            {@render previousAttempts(index)}
            {#if message.role === "user"}
                {#if message.checkpointId}
                    <div class="assistant-checkpoint d-flex align-items-center gap-2 small text-muted mb-1">
                        <i class="bi bi-bookmark" aria-hidden="true"></i>
                        {#if message.checkpointExpired}
                            <span>{m.assistant_checkpoint_expired()}</span>
                        {:else}
                            <span>{m.assistant_checkpoint_label()}</span>
                            <button
                                type="button"
                                class="btn btn-link btn-sm p-0 assistant-checkpoint-action"
                                title={m.assistant_checkpoint_restore_title()}
                                disabled={!restoreEnabled}
                                onclick={() => confirmRestoreToMessage(message.id)}
                            >
                                <i class="bi bi-arrow-counterclockwise me-1" aria-hidden="true"></i>{m.assistant_checkpoint_restore()}
                            </button>
                        {/if}
                    </div>
                {/if}
                <div class="assistant-user-row d-flex justify-content-end align-items-start gap-1 mb-3">
                    {#if message.checkpointId && !message.checkpointExpired}
                        <button
                            type="button"
                            class="btn btn-link btn-sm p-0 text-muted assistant-checkpoint-action"
                            title={m.assistant_checkpoint_edit_title()}
                            aria-label={m.assistant_checkpoint_edit()}
                            disabled={!restoreEnabled}
                            onclick={() => confirmRestoreToMessage(message.id, { edit: true })}
                        >
                            <i class="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                    {/if}
                    <div class="assistant-user-msg px-3 py-2">{message.content}</div>
                </div>
            {:else if message.role === "event"}
                <div class="assistant-restore-event text-center small text-muted mb-3">
                    <div class="d-flex align-items-center gap-2">
                        <hr class="flex-grow-1 my-0" />
                        <span>
                            <i class="bi bi-arrow-counterclockwise me-1" aria-hidden="true"></i>
                            {message.type === "redo_restore"
                                ? m.assistant_redo_restore_event()
                                : m.assistant_restore_event({ prompt: message.prompt ?? "" })}
                        </span>
                        <hr class="flex-grow-1 my-0" />
                    </div>
                    {#if message.skippedLockedGroups?.length}
                        <div>{m.assistant_restore_event_locked({ groups: message.skippedLockedGroups.join(", ") })}</div>
                    {/if}
                    {#if message.undoCheckpointId && !message.redone}
                        <button
                            type="button"
                            class="btn btn-link btn-sm p-0"
                            disabled={!restoreEnabled}
                            onclick={() => confirmRedoRestore(message.id)}
                        >
                            {message.type === "redo_restore" ? m.assistant_restore_again_button() : m.assistant_redo_restore_button()}
                        </button>
                    {/if}
                </div>
            {:else}
                <div class="mb-3 assistant-reply d-flex gap-2">
                    <div class="assistant-avatar flex-shrink-0" aria-hidden="true"><i class="bi bi-stars"></i></div>
                    <div class="flex-grow-1 assistant-reply-body">
                    {#if message.agentModel === "deep"}
                        <div class="mb-1">
                            <span class="badge rounded-pill text-bg-secondary assistant-deep-tag" title={m.assistant_model_deep_hint()}>
                                {m.assistant_reply_deep()}
                            </span>
                        </div>
                    {/if}
                    {#if message.activities?.length}
                        <div class="mb-2">
                            {#each message.activities as activity (activity.toolUseId)}
                                <AssistantActivityCard {activity} turnId={message.turnId} />
                            {/each}
                        </div>
                    {/if}
                    {#if message.content}
                        <div class="assistant-markdown">
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html renderAssistantMarkdown(message.content)}
                        </div>
                    {/if}
                    {#if message.streaming && (message.mode === "agent" || !message.content)}
                        <div class="text-muted small mt-1">
                            <span class="spinner-grow spinner-grow-sm me-2" aria-hidden="true"></span>
                            {message.mode === "agent" ? m.assistant_agent_working() : m.assistant_thinking()}
                        </div>
                    {/if}
                    {#if message.notice}
                        <div class="alert alert-warning small py-1 px-2 mt-2 mb-0" role="status">
                            {NOTICES[message.notice]?.() ?? message.notice}
                        </div>
                    {/if}
                    {#if message.undoSteps > 1}
                        <div class="text-muted small mt-1">{m.assistant_agent_undo_split({ count: message.undoSteps })}</div>
                    {/if}
                    {#if message.errorCode}
                        <div class="alert alert-danger small py-1 px-2 mt-2 mb-0" role="alert">
                            {errorText(message.errorCode)}
                        </div>
                    {/if}
                    {#if message.stopped}
                        <div class="text-muted small fst-italic mt-1">{m.assistant_stopped()}</div>
                    {/if}
                    {#if message.citations?.length}
                        <div class="mt-2 d-flex flex-wrap gap-1 align-items-center">
                            <small class="text-muted me-1">{m.assistant_sources()}:</small>
                            {#each message.citations as citation (citation.url)}
                                <a
                                    class="assistant-citation"
                                    href={citation.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={citation.title}
                                >
                                    <i class="bi bi-book me-1"></i>{citationLabel(citation)}
                                </a>
                            {/each}
                        </div>
                    {/if}
                    </div>
                </div>
            {/if}
        {/each}
        {@render previousAttempts(messages.length)}
    {/if}
    {#if assistantState.restoring}
        <div class="text-muted small text-center" role="status">
            <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>{m.assistant_restoring()}
        </div>
    {/if}
</div>

<style>
    /* Checkpoint actions appear on hover, like Cursor; keyboard focus shows them too. */
    .assistant-checkpoint-action {
        opacity: 0;
        font-size: 0.8rem;
        transition: opacity 0.15s;
    }

    .assistant-checkpoint:hover .assistant-checkpoint-action,
    .assistant-user-row:hover .assistant-checkpoint-action,
    .assistant-checkpoint-action:focus-visible {
        opacity: 1;
    }

    .assistant-attempt summary {
        cursor: pointer;
    }

    .assistant-user-msg {
        max-width: 85%;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 0.9rem;
        background-color: var(--bs-primary-bg-subtle);
        border: 1px solid var(--bs-primary-border-subtle);
        border-radius: 0.9rem 0.9rem 0.25rem 0.9rem;
    }

    .assistant-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.6rem;
        height: 1.6rem;
        margin-top: 0.1rem;
        border-radius: 50%;
        background-color: rgba(var(--bs-primary-rgb), 0.15);
        color: var(--bs-primary);
        font-size: 0.8rem;
    }

    .assistant-reply-body {
        min-width: 0;
    }

    .assistant-checkpoint {
        font-size: 0.75rem;
        opacity: 0.85;
    }

    .assistant-empty {
        margin-top: 2.5rem;
    }

    .assistant-empty-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 50%;
        background-color: rgba(var(--bs-primary-rgb), 0.15);
        color: var(--bs-primary);
        font-size: 1.3rem;
    }

    .assistant-suggestion {
        padding: 0.45rem 0.7rem;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: var(--bs-border-radius);
        background-color: var(--bs-tertiary-bg);
        color: var(--bs-body-color);
        font-size: 0.85rem;
        transition: border-color 0.15s, background-color 0.15s;
    }

    .assistant-saved-suggestion {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
    }

    .assistant-empty-heading {
        font-size: 0.75rem;
        font-weight: 600;
    }

    .assistant-suggestion:hover,
    .assistant-suggestion:focus-visible {
        border-color: var(--bs-primary);
        background-color: rgba(var(--bs-primary-rgb), 0.1);
        outline: none;
    }

    .assistant-suggestion i {
        color: var(--bs-primary);
    }

    .assistant-deep-tag {
        font-size: 0.7rem;
        font-weight: 500;
    }

    .assistant-citation {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 0.1rem 0.55rem;
        overflow: hidden;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: 999px;
        color: var(--bs-secondary-color);
        font-size: 0.75rem;
        text-decoration: none;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .assistant-citation:hover {
        border-color: var(--bs-primary);
        color: var(--bs-link-color);
    }

    .assistant-markdown {
        word-break: break-word;
        font-size: 0.9rem;
    }

    .assistant-markdown :global {
        p:last-child,
        ul:last-child,
        ol:last-child {
            margin-bottom: 0;
        }

        pre {
            background-color: var(--bs-tertiary-bg);
            padding: 0.5rem;
            border-radius: 0.25rem;
            white-space: pre-wrap;
        }

        code {
            font-size: 0.85em;
        }

        h1,
        h2,
        h3,
        h4 {
            font-size: 1rem;
            font-weight: 600;
        }
    }
</style>
